import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ProductCategory {
  id: number;
  name: string;
  description: string;
  productCount: string;
  icon: string;
  image: string;
  alt: string;
}

const productCategories: ProductCategory[] = [
{
  id: 6,
  name: 'Specialized Products',
  description: 'Oncology, biotechnology, and advanced therapeutic formulations',
  productCount: '400+',
  icon: 'AcademicCapIcon',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f64b6dd5-1767434498710.png",
  alt: 'Advanced pharmaceutical research equipment with specialized medication vials in modern laboratory'
},
{
  id: 5,
  name: 'Nutraceuticals',
  description: 'Dietary supplements and wellness products with scientific formulations',
  productCount: '500+',
  icon: 'HeartIcon',
  image: "https://images.unsplash.com/photo-1662673130628-13ab8b457619",
  alt: 'Colorful vitamin and supplement capsules in clear bottles with natural ingredients displayed'
},
{
  id: 2,
  name: 'Injectable Solutions',
  description: 'Sterile injectables meeting international quality standards and GMP requirements',
  productCount: '1200+',
  icon: 'CubeTransparentIcon',
  image: "https://images.unsplash.com/photo-1651493767793-9af7014ee121",
  alt: 'Medical injection vials and syringes with clear liquid medication in sterile pharmaceutical environment'
},
{
  id: 1,
  name: 'Tablets & Capsules',
  description: 'Comprehensive range of solid dosage forms with complete regulatory documentation',
  productCount: '2500+',
  icon: 'BeakerIcon',
  image: "https://images.unsplash.com/photo-1631980839613-61a08159d506",
  alt: 'White pharmaceutical tablets and capsules arranged on clean surface with medical packaging'
},
{
  id: 3,
  name: 'Syrups & Suspensions',
  description: 'Liquid formulations with precise dosing and extended stability profiles',
  productCount: '800+',
  icon: 'BeakerIcon',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_132a99c09-1765982643211.png",
  alt: 'Amber pharmaceutical syrup bottles with measuring cups and dosage instructions on white background'
},
{
  id: 4,
  name: 'Ointments & Creams',
  description: 'Topical preparations for dermatological and therapeutic applications',
  productCount: '600+',
  icon: 'SparklesIcon',
  image: "https://images.unsplash.com/photo-1574886722089-acdc1db5f133",
  alt: 'White pharmaceutical cream tubes and jars with medical packaging on clean surface'
}];


export default function ProductCategories() {
  return (
    <section className="py-12 bg-background">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full mb-3">
            <Icon name="CubeIcon" size={18} className="text-primary" />
            <span className="text-xs font-medium text-primary">Product Portfolio</span>
          </div>
          <h2 className="font-heading font-bold text-2xl lg:text-3xl text-textPrimary mb-3">
            Comprehensive Product Categories
          </h2>
          <p className="text-sm text-textSecondary max-w-2xl mx-auto">
            Explore our extensive range of pharmaceutical products across multiple therapeutic categories, all backed by international certifications and regulatory approvals.
          </p>
        </div>

        {/* Categories Grid - Single Line */}
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {productCategories.map((category) =>
            <div
              key={category.id}
              className="group bg-card rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-border w-64 flex-shrink-0">

                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden bg-gray-50">
                  <AppImage
                  src={category.image}
                  alt={category.alt}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />

                  <div className="absolute inset-0 bg-gradient-to-t from-textPrimary/60 to-transparent"></div>
                  <div className="absolute top-3 right-3 px-2.5 py-1 bg-background/90 backdrop-blur-sm rounded-full">
                    <span className="text-xs font-semibold text-primary">{category.productCount} Products</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg flex-shrink-0 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                      <Icon
                      name={category.icon as any}
                      size={20}
                      className="text-primary group-hover:text-white transition-colors duration-300" />

                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-base text-textPrimary mb-1.5 group-hover:text-primary transition-colors duration-300 break-words">
                        {category.name}
                      </h3>
                      <p className="text-xs text-textSecondary leading-relaxed line-clamp-2 break-words">{category.description}</p>
                    </div>
                  </div>

                  <Link
                  href="/product-trading-portal"
                  className="inline-flex items-center text-xs font-medium text-primary hover:text-accent transition-colors duration-300">

                    Explore Products
                    <Icon name="ArrowRightIcon" size={14} className="ml-1 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center mt-8">
          <a
            href="https://docs.google.com/spreadsheets/d/1QJVxOO0iT_LMb9zygy3Zaq7Mdq4p6D27tEMXjNhvNl0/edit?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-cta text-cta-foreground font-cta font-semibold text-sm rounded-md hover:-translate-y-1 hover:shadow-cta transition-all duration-300">

            View Complete Product Catalog
            <Icon name="ArrowRightIcon" size={18} className="ml-2" />
          </a>
        </div>
      </div>
    </section>);
}